import { Header } from '@react-navigation/elements';
import { CommonActions } from '@react-navigation/native';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, TouchableOpacity } from 'react-native';
import Toast from 'react-native-toast-notifications';

import CancelSubscriptionModal from '@components/Modals/CancelSubscriptionModal.web';
import CancelSubscriptionModal2 from '@components/Modals/CancelSubscriptionModal2.web';
import { ProjectDetailModal } from '@components/Modals/ProjectDetailModal.web';
import { TeamDetailModal } from '@components/Modals/TeamDetailModal.web';
import { WebModal } from '@components/Modals/WebModal.web';
import { RecentSearches } from '@components/RecentSearches/RecentSearches.web';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  getTitleRowWidth,
  nextMeasuredWidth,
} from '@components/Web/Navigator/drawerHeaderLayout';
import { DrawerHeaderProps } from '@components/Web/Navigator/WebDrawerNavigator';
import {
  Project,
  ProjectMemberRole,
  Team,
  TeamMemberRole,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useGlobalSearch from '@hooks/useGlobalSearch';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { useProjectMembership } from '@hooks/useProjectMembership';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import GlobalSearch from '@screens/Global/GlobalSearch.web';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { useLocation } from '@utils/locationUtils';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

// sessionStorage access is best-effort: the getter itself throws in Safari
// private mode and wherever storage is blocked, and the header must still
// render — and still be able to drop a team that no longer exists (TA-10850
// QA). Reading it at render time was previously unguarded, which took the
// whole web shell down in those environments.
const readSelectedTeamName = (): string | null => {
  try {
    return window.sessionStorage?.getItem(SESSION_SELECTED_TEAM_NAME) ?? null;
  } catch {
    return null;
  }
};

const writeSelectedTeamName = (value: string) => {
  try {
    window.sessionStorage?.setItem(SESSION_SELECTED_TEAM_NAME, value);
  } catch {
    // The name is a convenience for the next render, not state we rely on.
  }
};

const clearSelectedTeamName = () => {
  try {
    window.sessionStorage?.removeItem(SESSION_SELECTED_TEAM_NAME);
  } catch {
    // Nothing to clean up if the key was never readable to begin with.
  }
};

type DrawerHeaderTitleProps = {
  options: DrawerHeaderProps['options'];
  navigation: DrawerHeaderProps['navigation'];
  t: (key: string, options?: Record<string, unknown>) => string;
  headerTitle?: string;
  selectedProject?: Project;
  setSelectedProject: (project: Project | undefined) => void;
  selectedTeam?: Team;
  selectedTeamName?: string;
  measuredMainPanelWidth?: number;
  measuredRightPartWidth?: number;
  isSubscriptionDetail: boolean;
  renderTeamSubscription: () => React.ReactNode;
  nonMember: boolean;
  isMobileBrowser: boolean;
  isProjectPage: boolean | null;
  isTitleTruncated: boolean;
  titleRef: React.MutableRefObject<HTMLElement | null>;
  mainPanelWidth: number;
  maxTitleWidth: number;
  showSearchBox: boolean;
  openAddress: (
    address?: string,
    latitude?: number | null,
    longitude?: number | null
  ) => void;
};

const createDrawerHeaderTitle =
  ({
    options,
    navigation,
    t,
    headerTitle,
    selectedProject,
    setSelectedProject,
    selectedTeam,
    selectedTeamName,
    measuredMainPanelWidth,
    measuredRightPartWidth,
    isSubscriptionDetail,
    renderTeamSubscription,
    nonMember,
    isMobileBrowser,
    isProjectPage,
    isTitleTruncated,
    titleRef,
    mainPanelWidth,
    maxTitleWidth,
    showSearchBox,
    openAddress,
  }: DrawerHeaderTitleProps) =>
  ({ children }: { children: React.ReactNode }) => (
    <Box>
      <Box height={17} />
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='flex-start'
        pl={
          Dimensions.get('window').width > 1280 &&
          options.title !== t('models:activity.globalActivityLog')
            ? 'xs'
            : undefined
        }
        height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
        style={{
          width: getTitleRowWidth({
            mainPanelWidth: measuredMainPanelWidth,
            rightPartWidth: measuredRightPartWidth,
            gap: theme.spacing.xs,
          }),
        }}>
        {isSubscriptionDetail &&
          (selectedTeam || selectedTeamName) &&
          renderTeamSubscription()}
        {!(isSubscriptionDetail && (selectedTeam || selectedTeamName)) && (
          <>
            {headerTitle && (
              <Box marginHorizontal='xxs'>
                <WebIcon
                  accessibilityLabel='Back to projects'
                  color='grey04'
                  name='ChevronLeft'
                  onPress={() => {
                    setSelectedProject(undefined);
                    navigation.dispatch(
                      CommonActions.reset({
                        index: 0,
                        routes: [
                          {
                            name: 'projects-stack',
                            params: {
                              screen: 'allprojects',
                            },
                          },
                        ],
                      })
                    );
                  }}
                />
              </Box>
            )}

            {isProjectPage ? (
              <TooltipWebV3
                title={
                  <Text variant='labelSmall' color='white' numberOfLines={0}>
                    {children}
                  </Text>
                }
                placement='bottom-start'
                disableHoverListener={!isTitleTruncated}
                disableFocusListener={!isTitleTruncated}
                disableTouchListener={!isTitleTruncated}>
                <Box
                  style={{
                    maxWidth: mainPanelWidth - 340,
                  }}>
                  <Text
                    mr='xs'
                    ref={titleRef}
                    accessibilityLabel='Project title'
                    variant='heading2'
                    numberOfLines={1}>
                    {children}
                  </Text>
                </Box>
              </TooltipWebV3>
            ) : (
              <Text
                accessibilityLabel='Project title'
                variant='heading2'
                maxWidth={maxTitleWidth}
                numberOfLines={1}>
                {children}
              </Text>
            )}

            {selectedProject?.settings?.pin && isProjectPage && (
              <Icon
                name='Pin'
                color='grey03'
                width={16}
                height={16}
                marginHorizontal='xxs'
              />
            )}

            {headerTitle && !nonMember && !isMobileBrowser && (
              <>
                {selectedProject && (
                  <Box marginLeft='xs'>
                    <ProjectDetailModal
                      accessibilityLabel='Options'
                      project={selectedProject}
                      popPosition='bottom'
                      popSize='small'
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
      {!showSearchBox && headerTitle && selectedProject?.address && (
        <TouchableOpacity
          onPress={() =>
            selectedProject.address &&
            openAddress(
              selectedProject.address,
              selectedProject.latitude,
              selectedProject.longitude
            )
          }>
          <Text ml='xl' pl='xxs' variant='metadata' color='grey05'>
            {selectedProject.address}
          </Text>
        </TouchableOpacity>
      )}
      {!(!showSearchBox && headerTitle && selectedProject?.address) && (
        <Box height={17} />
      )}
    </Box>
  );

type DrawerHeaderRightProps = {
  options: DrawerHeaderProps['options'];
  route: DrawerHeaderProps['route'];
  t: (key: string, options?: Record<string, unknown>) => string;
  headerTitle?: string;
  selectedProject?: Project;
  isMobileBrowser: boolean;
  outsideClickRef: React.RefObject<HTMLDivElement | null>;
  setMeasuredRightPartWidth: (
    updater: (previous: number | undefined) => number | undefined
  ) => void;
  setSearchWidth: (width: number) => void;
  isHoveredSearch: boolean;
  setHoveredSearch: (hovered: boolean) => void;
  isNotTeamMember: boolean;
  disableGlobalSearch: boolean;
  setShowSearchBox: (show: boolean) => void;
  newTaskHoverProps: Record<string, unknown>;
  disableMode?: boolean | Project['archivedAt'];
  newTaskSurfaceColor: 'grey02' | 'grey05' | 'black';
  setPaywallTeam: (team: Team | undefined) => void;
  setPaywallModalOpen: (open: boolean) => void;
  setIdTaskDetailOpen: (id: string) => void;
  setIsTaskWebPanelOpen: (open: boolean) => void;
  setIdProjectOfTaskDetailOpen: (id: string) => void;
  setUUIdProjectOfTaskDetailOpen: (id: string) => void;
};

const createDrawerHeaderRight =
  ({
    options,
    route,
    t,
    headerTitle,
    selectedProject,
    isMobileBrowser,
    outsideClickRef,
    setMeasuredRightPartWidth,
    setSearchWidth,
    isHoveredSearch,
    setHoveredSearch,
    isNotTeamMember,
    disableGlobalSearch,
    setShowSearchBox,
    newTaskHoverProps,
    disableMode,
    newTaskSurfaceColor,
    setPaywallTeam,
    setPaywallModalOpen,
    setIdTaskDetailOpen,
    setIsTaskWebPanelOpen,
    setIdProjectOfTaskDetailOpen,
    setUUIdProjectOfTaskDetailOpen,
  }: DrawerHeaderRightProps) =>
  () => (
    <Box
      justifyContent='flex-end'
      mt='s'
      mb='s'
      onLayout={(e) => {
        // TA-10618: same 0-width layout on blur as the main panel.
        const { width } = e.nativeEvent.layout;
        setMeasuredRightPartWidth((previous) =>
          nextMeasuredWidth(previous, width)
        );
      }}>
      <Box flex={1} />
      <Box flexDirection='row'>
        <Box style={{ width: 0, height: 0 }} ref={outsideClickRef} />
        {/*
          TA-10618: the Global Search icon used to live in the header
          title row, whose width is computed from onLayout
          measurements — so on every screen transition it painted next
          to the page title for a frame before the real width landed.
          It now sits in headerRight next to New Task, laid out by
          flexbox alone, which is why New Task never flashed.

          Spacing is `mr='xxxs'` (2px) plus New Task's own `ml='xs'`.
          On paper the old title row also contributed its reserved
          `theme.spacing.xs` and the title container's 16px margin,
          but that row overflowed the header, so none of it showed up
          as visible gap — adding it back here (`mr='m'`) made the
          spacing visibly wider than before. Verified against the
          running app: keep `xxxs`.
        */}
        {!isMobileBrowser && (
          <>
            <Box
              justifyContent='center'
              mr='xxxs'
              onLayout={(e) => setSearchWidth(e.nativeEvent.layout.width)}>
              <Box
                alignItems='center'
                justifyContent='center'
                style={{ height: 32, width: 32 }}
                borderRadius='xs'
                onMouseEnter={() => !isNotTeamMember && setHoveredSearch(true)}
                onMouseLeave={() => !isNotTeamMember && setHoveredSearch(false)}
                paddingHorizontal='xs'
                paddingVertical='xs'
                backgroundColor={isHoveredSearch ? 'grey01' : undefined}>
                <TouchableOpacity
                  disabled={disableGlobalSearch}
                  accessibilityLabel='Global Search'
                  onPress={() => {
                    if (!disableGlobalSearch) {
                      setShowSearchBox(true);
                    }
                  }}
                  style={{
                    opacity: 1,
                    cursor: disableGlobalSearch ? 'not-allowed' : 'pointer',
                  }}>
                  <Icon
                    color={disableGlobalSearch ? 'grey04' : 'textPrimary'}
                    variant='l'
                    name='Search'
                  />
                </TouchableOpacity>
              </Box>
            </Box>
            <Box justifyContent='center' flexDirection='row'>
              <UpgradePaywallTooltip
                show={
                  !!(
                    selectedProject &&
                    shouldShowPaywallForProject(selectedProject)
                  )
                }>
                <TouchableOpacity
                  {...newTaskHoverProps}
                  disabled={disableMode || isNotTeamMember}
                  onPress={() => {
                    // Check paywall for selected project or primary team
                    if (selectedProject) {
                      if (shouldShowPaywallForProject(selectedProject)) {
                        setPaywallTeam(selectedProject?.team ?? undefined);
                        setPaywallModalOpen(true);
                        return;
                      }
                    }
                    setIdTaskDetailOpen('');
                    setIsTaskWebPanelOpen(true);
                    if (
                      (selectedProject &&
                        route.name === 'projects-stack' &&
                        (selectedProject?.name ?? '')) ===
                      (headerTitle ??
                        options.title ??
                        options.drawerLabel ??
                        route.name)
                    ) {
                      setIdProjectOfTaskDetailOpen(selectedProject?.id ?? '');
                      setUUIdProjectOfTaskDetailOpen(
                        selectedProject?.uuid ?? ''
                      );
                    } else {
                      setIdProjectOfTaskDetailOpen('');
                      setUUIdProjectOfTaskDetailOpen('');
                    }
                  }}
                  accessibilityLabel={t('models:tasks.buttons.newTask')}>
                  <Box
                    mr={Dimensions.get('window').width > 1280 ? 'l' : 'm'}
                    width={122}
                    height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                    flexDirection='row'
                    borderWidth={1}
                    backgroundColor={newTaskSurfaceColor}
                    borderColor={newTaskSurfaceColor}
                    borderRadius='m'
                    alignItems='center'
                    px='s'
                    ml='xs'>
                    <Box
                      width={16}
                      height={16}
                      marginRight='xxs'
                      alignItems='center'
                      justifyContent='center'
                      pointerEvents='none'>
                      <Icon
                        variant='m'
                        name='Plus'
                        color={
                          disableMode || isNotTeamMember ? 'grey06' : 'white'
                        }
                      />
                    </Box>
                    <Box
                      alignItems='center'
                      justifyContent='center'
                      style={{ flex: 1 }}>
                      <Text
                        variant='labelSmall'
                        color={
                          disableMode || isNotTeamMember ? 'grey06' : 'white'
                        }>
                        {t('models:tasks.buttons.newTask')}
                      </Text>
                    </Box>
                  </Box>
                </TouchableOpacity>
              </UpgradePaywallTooltip>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

export const DrawerHeader = ({
  layout,
  navigation,
  options,
  route,
}: DrawerHeaderProps) => {
  const {
    headerTitle,
    selectedProject,
    setSelectedProject,
    selectedTeam,
    setSelectedTeam,
  } = useWebDrawerNavigator();

  const { me } = useMe();

  const path = window.location.pathname;
  const selectedTeamNametmp = readSelectedTeamName();
  let fromTeamDetailPage = false;
  let selectedTeamName: string | undefined = undefined;
  if (selectedTeamNametmp && selectedTeamNametmp.length > 1) {
    fromTeamDetailPage = selectedTeamNametmp[0] === '2';
    selectedTeamName = selectedTeamNametmp.substring(1);
  }
  const subscriptionDetailPagePath = '/subscriptionPlanDetail';

  const regexProjectDetailPage =
    /^\/?projects\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?:\/[^/]+)?$/;
  const matchProjectPage = path.match(regexProjectDetailPage);
  const isProjectPage = matchProjectPage && matchProjectPage?.length >= 1;

  // Regex to match subscriptionPlanDetail route: /my-account/subscriptionPlanDetail/:teamUUId/:screen?
  const regexSubscriptionPlanDetailPage =
    /^\/?my-account\/subscriptionPlanDetail\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?:\/[^/]+)?$/;
  const matchSubscriptionPlanDetailPage = path.match(
    regexSubscriptionPlanDetailPage
  );
  const isSubscriptionPlanDetailPage = !!(
    matchSubscriptionPlanDetailPage &&
    matchSubscriptionPlanDetailPage?.length >= 1
  );

  const { isMemberOrOwner } = useProjectMembership(selectedProject);
  const [
    showCancelSubscriptionConfirmBox,
    setShowCancelSubscriptionConfirmBox,
  ] = useState(false);
  const [
    showCancelSubscriptionConfirmBox2,
    setShowCancelSubscriptionConfirmBox2,
  ] = useState(false);

  const isTeamOwnerOrAdmin =
    selectedProject?.currentUserTeamRole === 'OWNER' ||
    selectedProject?.currentUserTeamRole === 'ADMIN';

  const isTeamMember =
    selectedProject?.currentUserTeamRole === TeamMemberRole.Member;

  const nonMember = !(isMemberOrOwner || isTeamOwnerOrAdmin || isTeamMember);

  const { t } = useTranslation();
  const { openAddress } = useLocation();

  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setUUIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setUpgradeTeamPlanOpen,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setUpgradeCTA,
    setPaywallModalOpen,
    setPaywallTeam,
  } = useWebDrawer();
  const { drawerCollapsed } = useActiveChat();

  useEffect(() => {
    setPopupLeftPosition(popupLeftPosition - (drawerCollapsed ? 170 : -170));
  }, [drawerCollapsed]);

  useEffect(() => {
    setIsTitleTruncated(false);
    const el = titleRef.current;
    if (!el) return;
    setIsTitleTruncated(el.scrollWidth > el.clientWidth);
  }, [headerTitle]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const {
    isSearchFocus,
    setIsSearchFocus,
    search,
    setSearch,
    storeRecentSearchTerm,
    setSearchWidth,
  } = useGlobalSearch();

  const refGlobalSearchBox = useRef<HTMLDivElement>(null);
  const ref1 = useRef<HTMLDivElement>(null);
  const ref2 = useRef<HTMLDivElement | undefined>(undefined);
  const isEventPointInModal = (event: MouseEvent) => {
    const rect = refGlobalSearchBox?.current?.getBoundingClientRect();
    const rect2 = ref1?.current?.getBoundingClientRect();
    const rect3 = ref2.current?.getBoundingClientRect();
    return (
      (rect &&
        event.x > rect.x &&
        event.x < rect.x + rect.width &&
        event.y > rect.y &&
        event.y < rect.y + rect.height) ||
      (rect2 &&
        event.x > rect2.x &&
        event.x < (rect?.x ?? 0) + (rect?.width ?? 0) &&
        event.y > rect2.y &&
        event.y < (rect3 ? rect3.y + (rect3?.height ?? 0) : 570))
    );
  };
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    const rect = refGlobalSearchBox?.current?.getBoundingClientRect();
    if ((rect?.x ?? 0) > 0 && !isEventPointInModal(event)) {
      setIsSearchFocus(false);
      setShowSearchBox(false);
      storeRecentSearchTerm();
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  const maxTitleWidth = 340;
  const minMainPanelWidth = 440;
  // TA-10618: both header measurements stay `undefined` until a real
  // onLayout lands, so the title row can tell "not measured yet" apart from a
  // genuinely narrow header. See drawerHeaderLayout.ts.
  const [measuredMainPanelWidth, setMeasuredMainPanelWidth] =
    useState<number>();
  const mainPanelWidth = measuredMainPanelWidth ?? minMainPanelWidth;
  const isMobileBrowser =
    typeof window !== 'undefined' &&
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent
    );

  // measuredRightPartWidth -- the headerRight cluster: the Global Search icon
  // (moved here in TA-10618) and the New Task button. No seed: the title row
  // only subtracts it once it is really measured.
  const [measuredRightPartWidth, setMeasuredRightPartWidth] =
    useState<number>();
  const [showSearchBox, setShowSearchBox] = useState<boolean>(false);
  const [isHoveredSearch, setHoveredSearch] = useState<boolean>(false);
  const [popupLeftPosition, setPopupLeftPosition] = useState<number>(212);
  const toastRef = useRef<any>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const onCloseSearch = () => {
    setSearch('');
    setIsSearchFocus(false);
    storeRecentSearchTerm();
    setShowSearchBox(false);
  };

  document.onkeydown = function (evt) {
    if (evt.key == 'Escape') {
      // Escape key pressed
      onCloseSearch();
    }
  };

  const isTeamOwner = selectedTeam?.owner?.id === me?.id;

  // getPublicTeam, not getTeam: `selectedTeam.id` here can carry the uuid of a
  // team the user is not a member of (TeamDetailPage sets selectedTeam from
  // the PublicTeam branch for invite-link visitors), and getTeam answers
  // "The requested team could not be found" for those. getPublicTeam resolves
  // for both, so the header renders the team name instead of logging an error.
  const {
    fullTeam: team,
    publicTeam,
    isNotFound: isTeamMissingError,
    loading: teamLoading,
    error: teamError,
    refetch,
  } = useTeamDetailFromQuery({
    // uuid first (PR #5885 review): listTeams returns `id` and `uuid` as
    // distinct fields, and every other detail reader keys this query by uuid
    // — TeamDetailPage passes the route's teamUUId. Keying the header by `id`
    // therefore addresses a different cache entry than the result those
    // readers just fetched, costing an extra request per team switch.
    // `?? id` keeps the previous behaviour for a selectedTeam that carries no
    // uuid, rather than silently skipping the query for it.
    teamUUId: selectedTeam?.uuid ?? selectedTeam?.id,
    // TA-10471: cache-first, matching TA-9875/TA-10450. DrawerHeader is a
    // persistent web shell component, so network-only meant a full team
    // refetch on every team switch and every shell remount. Team field
    // updates (name/address/subscriptionDetails) still land here via Apollo
    // normalisation of the Team entity — TeamDetailPage and
    // SubscriptionPlanDetail.web still fetch network-only for the same team.
    fetchPolicy: 'cache-first',
    // Matches the other team-detail readers, so cache-first actually hits the
    // entry they populated instead of opening a second one.
    includePending: true,
    // TA-10847: the header reads name/address/role/subscription only. Keep
    // in step with MainPanel.web so both shell readers share one request.
    withMembers: false,
  });

  const isTeamDetailPage = isSubscriptionPlanDetailPage;
  // `== null`, not `=== undefined`: Team.currentUserRole is Maybe<TeamRole>,
  // so an invite viewer's Team response can carry an explicit null — which a
  // strict undefined check reads as "has a role", leaving that viewer's team
  // actions and New Task control enabled.
  const isNotTeamMember = isTeamDetailPage && team?.currentUserRole == null;

  // The team is really gone (deleted, or the id is stale) only when the query
  // has settled, raised no error, and answered with neither branch of the
  // union. Previously this was driven by `teamError`, which getPublicTeam no
  // longer raises for a non-member — that path now resolves to the PublicTeam
  // branch instead.
  //
  // `!teamError` matters as much as `!teamLoading`: on an offline blip or a
  // 5xx with nothing cached, Apollo settles loading to false with both
  // branches empty, which is indistinguishable from a deleted team unless the
  // error is checked. Without it this still dropped the header name and the
  // persisted team for a team that is fine.
  const teamNotResolved =
    !!(selectedTeam?.uuid ?? selectedTeam?.id) &&
    !teamLoading &&
    !teamError &&
    !team &&
    !publicTeam;

  // `isNotFound` comes from the hook (TA-10850 QA): only a not-found error
  // means the team is actually gone, and the backend sends that as an HTTP
  // 401 whose top-level Apollo message is generic — a substring check here
  // missed it, leaving a deleted team in the drawer and in sessionStorage.
  // Every other error is transient and must not clear the selection.

  useEffect(() => {
    if (
      (isTeamMissingError || teamNotResolved) &&
      selectedTeam &&
      !isSubscriptionPlanDetailPage
    ) {
      setSelectedTeam(undefined);
      clearSelectedTeamName();
    }
  }, [
    isTeamMissingError,
    teamNotResolved,
    selectedTeam,
    setSelectedTeam,
    isSubscriptionPlanDetailPage,
  ]);

  useEffect(() => {}, [
    team?.subscriptionDetails,
    selectedTeam?.subscriptionDetails,
    refetch,
  ]);

  const goBackFromSubscriptionDetail = () => {
    selectedTeam && setSelectedTeam(undefined);
    writeSelectedTeamName('');
    if (fromTeamDetailPage === true) {
      navigation.navigate('my-account', {
        screen: 'tabs',
        params: {
          screen: 'my-profile',
        },
      });
    } else {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            {
              name: 'my-account',
              params: {
                screen: 'tabs',
                params: { screen: 'subscription-plans' },
              },
            },
          ],
        })
      );
    }
  };

  const { idTeamDelete, setIdTeamDelete } = useWebDrawer();
  useEffect(() => {
    if (!team?.subscriptionDetails && idTeamDelete !== '') {
      setIdTeamDelete('');
      goBackFromSubscriptionDetail();
    }
  }, [idTeamDelete]);
  const renderTeamSubscription = () => {
    return (
      <>
        <Box marginHorizontal='xxs'>
          <WebIcon
            accessibilityLabel='Go back'
            color='grey04'
            variant='s'
            name='ChevronLeft'
            onPress={goBackFromSubscriptionDetail}
          />
        </Box>
        <Box flexDirection='row' alignItems='center'>
          <Box marginEnd='xs'>
            <Text
              accessibilityLabel='Team Name'
              variant='heading2'
              numberOfLines={1}
              style={{ maxWidth: maxTitleWidth }}>
              {team?.name ?? selectedTeam?.name}
            </Text>
            <Text
              accessibilityLabel='Team Address'
              variant='metadata'
              numberOfLines={1}
              style={{ maxWidth: maxTitleWidth }}>
              {team?.address ?? selectedTeam?.address}
            </Text>
          </Box>
          {!isNotTeamMember && (
            <TeamDetailModal
              popSize='big'
              name={team?.name ?? selectedTeam?.name}
              id={selectedTeam?.id}
              teamUUId={selectedTeam?.uuid}
              openEditSubscription={() => {
                setIsTaskWebPanelOpen(false);
                setIsTaskWebPanelMaximize(false);
                setIdTaskDetailOpen('');
                setIdProjectOfTaskDetailOpen('');
                setUUIdProjectOfTaskDetailOpen('');
                setIsCreateProjectOpen(false);
                setEditProjectUUId('');
                setUpgradeTeamPlanOpen(true);
                setIdTeamOpen(selectedTeam?.id ?? '');
                setUUIdTeamOpen(selectedTeam?.uuid ?? '');
                setIsTeamPersonal(selectedTeam?.personal ?? false);
                setUpgradeCTA(false);
              }}
              doCancelSubscription={() => {
                setShowCancelSubscriptionConfirmBox(true);
              }}
              canEdit={
                isTeamOwner &&
                (selectedTeam?.subscriptionDetails?.isSubscriptionActive ||
                  selectedTeam?.subscriptionDetails?.isSubscriptionOnTrial) &&
                !selectedTeam?.personal
              }
              isPaid={
                selectedTeam?.subscriptionDetails?.paymentCardLast4 !== '' ||
                selectedTeam?.subscriptionDetails?.paymentCardLast4 !== null
              }
            />
          )}
        </Box>
      </>
    );
  };

  const isSubscriptionDetail = path.includes(subscriptionDetailPagePath);
  const isViewer =
    selectedProject?.currentUserRole === ProjectMemberRole.Viewer;
  const disableMode =
    selectedProject &&
    (selectedProject?.archivedAt ||
      isViewer ||
      !(isMemberOrOwner || isTeamMember || isTeamOwnerOrAdmin));
  const disableGlobalSearch = isNotTeamMember || !!disableMode;
  // New Task button hover (TA-9005 / Figma button sheet): a fill button
  // shifts bg AND border to grey05 on hover. Hook lives here because the
  // button renders inside the headerRight callback, which cannot own hooks.
  const { isHovered: isNewTaskHovered, hoverProps: newTaskHoverProps } =
    useHover({ disabled: !!disableMode || isNotTeamMember });
  const newTaskSurfaceColor = (() => {
    if (disableMode || isNotTeamMember) return 'grey02' as const;
    return isNewTaskHovered ? ('grey05' as const) : ('black' as const);
  })();

  const renderHeaderTitle = createDrawerHeaderTitle({
    options,
    navigation,
    t,
    headerTitle,
    selectedProject,
    setSelectedProject,
    selectedTeam,
    selectedTeamName,
    measuredMainPanelWidth,
    measuredRightPartWidth,
    isSubscriptionDetail,
    renderTeamSubscription,
    nonMember,
    isMobileBrowser,
    isProjectPage,
    isTitleTruncated,
    titleRef,
    mainPanelWidth,
    maxTitleWidth,
    showSearchBox,
    openAddress,
  });

  const renderHeaderRight = createDrawerHeaderRight({
    options,
    route,
    t,
    headerTitle,
    selectedProject,
    isMobileBrowser,
    outsideClickRef: ref,
    setMeasuredRightPartWidth,
    setSearchWidth,
    isHoveredSearch,
    setHoveredSearch,
    isNotTeamMember,
    disableGlobalSearch,
    setShowSearchBox,
    newTaskHoverProps,
    disableMode,
    newTaskSurfaceColor,
    setPaywallTeam,
    setPaywallModalOpen,
    setIdTaskDetailOpen,
    setIsTaskWebPanelOpen,
    setIdProjectOfTaskDetailOpen,
    setUUIdProjectOfTaskDetailOpen,
  });

  return (
    <>
      <Box
        style={{ minWidth: minMainPanelWidth }}
        onLayout={(e) => {
          // TA-10618: ResourceSavingView hides blurred screens with
          // `display: none`, and RN-web reports that as a 0-width layout.
          // Storing that 0 collapses the title row, so the next focus paints
          // a collapsed header. Keep the last real measurement instead.
          const { width } = e.nativeEvent.layout;
          setMeasuredMainPanelWidth((previous) =>
            nextMeasuredWidth(previous, width)
          );
        }}>
        <Header
          {...options}
          headerStyle={{
            height: 73,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.grey02,
          }}
          layout={layout}
          title={
            headerTitle ?? options.title ?? options.drawerLabel ?? route.name
          }
          headerTitleContainerStyle={{ right: 0 }}
          headerTitle={renderHeaderTitle}
          headerRight={renderHeaderRight}
        />
        <Box borderBottomWidth={1} borderBottomColor='grey02'></Box>
      </Box>

      <CancelSubscriptionModal2
        visible={showCancelSubscriptionConfirmBox}
        onClose={() => setShowCancelSubscriptionConfirmBox(false)}
        selectedTeam={selectedTeam}
        setShowCancelSubscriptionConfirmBox2={(value: boolean) =>
          setShowCancelSubscriptionConfirmBox2(value)
        }
      />

      <CancelSubscriptionModal
        visible={showCancelSubscriptionConfirmBox2}
        onClose={() => setShowCancelSubscriptionConfirmBox2(false)}
        selectedTeam={selectedTeam}
        afterSubmitCallback={goBackFromSubscriptionDetail}
        navigation={navigation}
        toastRef={toastRef}
      />

      <Toast
        ref={toastRef}
        renderToast={() => {
          return (
            <Box
              borderRadius='xs'
              height={80}
              p='s'
              flex={1}
              backgroundColor='black'
              mb='m'>
              <Box flexDirection='row' padding='xxs'>
                <Box flexDirection='row' alignItems='center' gap='xs'>
                  <Icon name='Check' width={24} height={24} color='white' />
                  <Text variant='heading2' color='white'>
                    {t('models:profile.subscriptionPlans.cancelledKeepAccess', {
                      date: dayjs(
                        team?.subscriptionDetails?.expirationDate
                      )?.format('MMM DD'),
                    })}
                  </Text>
                </Box>
              </Box>
            </Box>
          );
        }}
      />
      <WebModal
        accessibilityLabel='search-box'
        visible={showSearchBox}
        width={820}
        height={700}
        title='Global Search'
        titleVariant='labelEmphasized'
        titleColor='textPrimary'
        marginHorizontal='l'
        onClose={() => onCloseSearch()}>
        <Box flex={1} mt='xs' pt='l' pb='m'>
          <Box mx='l' accessibilityLabel='Search input'>
            <SearchInput
              autoFocus={showSearchBox}
              value={search}
              onTextChanged={(value: string) => {
                setSearch(value);
              }}
              onFocus={() => {
                !isSearchFocus && setIsSearchFocus(true);
              }}
              onCancel={() => {
                setSearch('');
                setIsSearchFocus(false);
                storeRecentSearchTerm();
              }}
              onEscPress={() => onCloseSearch()}
              placeHolder='Find Tasks, Projects or Files'
              height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
            />
          </Box>
          <Box flex={1} mt='l'>
            {search.length === 0 && <RecentSearches boxRef={ref2} />}
            <GlobalSearch
              visible={search.length > 0}
              doAfterPressItem={() => {
                setShowSearchBox(false);
              }}
            />
          </Box>
        </Box>
      </WebModal>
    </>
  );
};
